import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auth, db, API_BASE_URL } from '../../firebase/firebaseConfig';
import {
  collection, doc, addDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  X, CreditCard, CheckCircle, AlertTriangle, XCircle, RotateCcw, StickyNote, PlayCircle, Ban,
} from 'lucide-react';

// Admin views mix currencies, so cad is 'CA$' to tell it apart from usd.
const CURRENCY_SYMBOL = { gbp: '£', usd: '$', eur: '€', cad: 'CA$' };
const TIER_LABEL = { warm: 'Warm (up to 3 locations)', glow: 'Glow (up to 5 locations)' };
// NOTE: no price table here — headline prices come from the billingCheck
// report (`tierHeadlines`), whose single source of truth is
// cloud/cloudfunction/functions/billing/billingConfig.js.

function fmtMoney(minor, currency) {
  if (minor === null || minor === undefined || isNaN(minor)) return '—';
  const cur = (currency || '').toLowerCase();
  const sym = CURRENCY_SYMBOL[cur] || '';
  const amount = (minor / 100).toFixed(2);
  return sym ? `${sym}${amount}` : `${amount} ${(currency || '').toUpperCase()}`;
}

function fmtDate(ms) {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return String(ms);
  }
}

function toDateInputValue(ms) {
  try { return new Date(ms).toISOString().slice(0, 10); } catch { return ''; }
}

const STATUS_META = {
  pass: { Icon: CheckCircle, cls: 'text-green-600' },
  warn: { Icon: AlertTriangle, cls: 'text-amber-600' },
  fail: { Icon: XCircle, cls: 'text-red-600' },
};

function CheckRow({ check }) {
  const meta = STATUS_META[check.status] || STATUS_META.warn;
  const { Icon, cls } = meta;
  return (
    <div className="flex items-start gap-2 py-1">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${cls}`} />
      <div className="min-w-0">
        <span className="text-sm">{check.label}</span>
        {check.detail && <span className="text-xs text-muted-foreground ml-2 break-all">{check.detail}</span>}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3 py-0.5">
      <span className="w-44 flex-shrink-0 text-xs text-muted-foreground pt-0.5">{label}</span>
      <div className="text-sm flex-1 min-w-0 break-all">{children}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-sm font-semibold mt-4 mb-1.5 border-b pb-1">{children}</div>;
}

const SUB_STATE_BADGE = {
  none: { label: 'No subscription', variant: 'outline' },
  scheduled: { label: 'Scheduled', variant: 'secondary' },
  active: { label: 'Active', variant: 'default' },
  // We never create trials; a Stripe 'trialing' status here would be unexpected.
  trialing: { label: 'Active (trialing — unexpected)', variant: 'default' },
  past_due: { label: 'Payment past due', variant: 'destructive' },
  unpaid: { label: 'Unpaid', variant: 'destructive' },
  incomplete: { label: 'Payment incomplete', variant: 'destructive' },
  canceled: { label: 'Canceled', variant: 'outline' },
};

export default function BillingModal({ accountUUID, accountEmail, onClose, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  // Setup form
  const [tier, setTier] = useState('');
  const [discountStr, setDiscountStr] = useState('');
  const [startDate, setStartDate] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createdOk, setCreatedOk] = useState(false);

  // Visibility toggle
  const [visible, setVisible] = useState(false);
  const [visibleSaving, setVisibleSaving] = useState(false);
  const [visibleError, setVisibleError] = useState(null);

  // Cancellation record (our bookkeeping of a cancellation done in Stripe)
  const [cancelDate, setCancelDate] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  // Notes
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [notesLoadError, setNotesLoadError] = useState(false);
  const [noteSaveError, setNoteSaveError] = useState(null);

  // A signed-out/expired admin session must surface as a message, not a TypeError.
  const getToken = useCallback(async () => {
    if (!auth.currentUser) throw new Error('Your admin session has expired — please sign in again.');
    return auth.currentUser.getIdToken(true);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setConfirming(false);
    setCreateError(null);
    try {
      const idToken = await getToken();
      const res = await fetch(`${API_BASE_URL}/admin/billingCheck`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ accountUUID }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Request failed (${res.status})`);
      setReport(data.report);
      setVisible(!!data.report.account.billingInfoVisible);

      // Pre-populate the setup form from the proposal (only when not yet chosen).
      const p = data.report.proposal;
      if (p) {
        // No default for over-cap accounts (recTier null) — force a deliberate choice.
        setTier((t) => t || p.recTier || '');
        setDiscountStr((d) => (d !== '' ? d : p.discountPct != null ? String(p.discountPct) : '0'));
        setStartDate((s) => {
          if (s) return s;
          const expired = !!(data.report.stripe && data.report.stripe.paymentMethod
            && data.report.stripe.paymentMethod.expired);
          if (!expired) {
            const tomorrow = Date.now() + 24 * 3600 * 1000;
            const def = p.defaultStartMs && p.defaultStartMs > Date.now() ? p.defaultStartMs : tomorrow;
            return toDateInputValue(def);
          }
          // An expired card has to be replaced by the customer before the first
          // payment, which means receiving an email, signing in and completing
          // a Stripe portal flow. Default to a week so accepting the pre-filled
          // date is the safe choice; one day is merely the hard floor.
          const weekOut = Date.now() + 7 * 24 * 3600 * 1000;
          const def = p.defaultStartMs && p.defaultStartMs > weekOut ? p.defaultStartMs : weekOut;
          return toDateInputValue(def);
        });
      }
    } catch (e) {
      setError(e.message);
      setReport(null);
    }
    setLoading(false);
  }, [accountUUID, getToken]);

  useEffect(() => { load(); }, [load]);

  // Live notes stream (admin-only collection).
  useEffect(() => {
    const q = query(
      collection(db, 'accountNotes', accountUUID, 'notes'),
      orderBy('createdAt', 'desc'),
    );
    const detach = onSnapshot(q, (snap) => {
      setNotesLoadError(false);
      setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.warn('accountNotes listener error', err.code);
      setNotesLoadError(true);
    });
    return () => detach();
  }, [accountUUID]);

  const handleVisibilityToggle = async (newValue) => {
    setVisibleSaving(true);
    setVisibleError(null);
    try {
      await updateDoc(doc(db, 'accountsInfo', accountUUID), { billingInfoVisible: newValue });
      setVisible(newValue);
      if (onChanged) onChanged();
    } catch (e) {
      console.error('Failed to update billingInfoVisible:', e);
      setVisibleError('The change did NOT save' +
        (e.code === 'permission-denied' ? ' (permission denied — are the latest Firestore rules deployed?)' : ` (${e.code || e.message})`) +
        '. Try again.');
    }
    setVisibleSaving(false);
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text || noteSaving) return;
    setNoteSaving(true);
    setNoteSaveError(null);
    try {
      await addDoc(collection(db, 'accountNotes', accountUUID, 'notes'), {
        text,
        adminEmail: auth.currentUser?.email || 'unknown',
        createdAt: serverTimestamp(),
      });
      setNoteText('');
    } catch (e) {
      console.error('Failed to add note:', e);
      setNoteSaveError('The note was NOT saved' +
        (e.code === 'permission-denied' ? ' (permission denied — are the latest Firestore rules deployed?)' : ` (${e.code || e.message})`) +
        '. Your text is kept above — try again.');
    }
    setNoteSaving(false);
  };

  // Record — or clear — that this subscription was cancelled. Bookkeeping
  // only: the cancellation itself happens in the Stripe dashboard.
  const handleSetCanceled = async ({ clear }) => {
    if (cancelSaving) return;
    setCancelSaving(true);
    setCancelError(null);
    try {
      const idToken = await getToken();
      const res = await fetch(`${API_BASE_URL}/admin/setSubscriptionCanceled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(clear
          ? { accountUUID, clear: true }
          : { accountUUID, canceledDate: cancelDate, reason: cancelReason.trim() || undefined }),
      });
      // Defensive parse: before the cloud functions are deployed this route
      // 404s with an HTML body, and a raw JSON SyntaxError tells the admin
      // nothing useful.
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !data.success) {
        throw new Error((data && data.error)
          || (res.status === 404
            ? 'This endpoint is not available — the cloud functions may not be deployed yet.'
            : `Request failed (${res.status})`));
      }
      setCancelOpen(false);
      setCancelReason('');
      if (onChanged) onChanged(); // refresh the dashboard's attention dots
      await load();
    } catch (e) {
      setCancelError(e.message);
    }
    setCancelSaving(false);
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const idToken = await getToken();
      const res = await fetch(`${API_BASE_URL}/admin/createSubscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          accountUUID,
          tier,
          discountPercent: Number(discountStr),
          startDate,
          overCapConfirmed: overCap === true,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Request failed (${res.status})`);
      setConfirming(false);
      // Mark success BEFORE reloading the report: if the reload fails the admin
      // must still see that the create itself worked.
      setCreatedOk(true);
      if (onChanged) onChanged();
      await load(); // re-pull the report — now shows the scheduled subscription
    } catch (e) {
      setCreateError(e.message);
    }
    setCreating(false);
  };

  const p = report && report.promised;
  const prop = report && report.proposal;
  const s = report && report.stripe;
  const state = report ? report.subscriptionState : 'none';
  const stateBadge = SUB_STATE_BADGE[state] || SUB_STATE_BADGE.none;

  // Our own record of a cancellation performed in Stripe. Separate from
  // `state`, which is discovered live from Stripe on every load.
  const canceled = report && report.account && report.account.canceledAtMs != null
    ? {
      atMs: report.account.canceledAtMs,
      recordedAtMs: report.account.canceledRecordedAtMs,
      byEmail: report.account.canceledByEmail,
      reason: report.account.canceledReason,
    }
    : null;
  // Only offer the record where a subscription was actually set up. This comes
  // from the server, computed with the very predicate the endpoint enforces
  // (billing/stripeState.js hasStoredSubscription), so the button can never be
  // offered where the write would be refused. Deriving it from live Stripe
  // state instead would disagree exactly in the reconciliation case below.
  const hasSubscriptionRecord = !!(report && report.account && report.account.hasStoredSubscription);
  // Stripe says cancelled, but this account has no subscription recorded
  // against it — there is nothing here to mark, and the mismatch itself is
  // what needs attention.
  // `&& !canceled`: once a record exists, the banner below reports it — the two
  // must never both render, or the modal says "nothing to mark as cancelled"
  // directly above "Recorded as cancelled".
  const canceledButUnlinked = state === 'canceled' && !hasSubscriptionRecord && !canceled;

  // Live preview of the setup form. Headline prices come from the server
  // report so the confirmed figure can never drift from what will be charged.
  const preview = useMemo(() => {
    const currency = report && report.proposal ? report.proposal.currency : null;
    const headlines = report ? report.tierHeadlines : null;
    const headline = tier && headlines ? headlines[tier] : null;
    const pct = Number(discountStr);
    if (headline == null || isNaN(pct) || pct < 0 || pct > 100) return null;
    const monthly = Math.round(headline * (1 - pct / 100));
    return { headline, pct, monthly, currency };
  }, [tier, discountStr, report]);

  const previewMatchesPromise = preview && p && preview.monthly === p.monthlyChargeMinor;

  // Over-cap: setting up a tier below the account's household count needs an
  // explicit, visible override (the server refuses without the flag).
  const tierCap = tier === 'warm' ? 3 : tier === 'glow' ? 5 : null;
  const overCap = !!(tierCap && prop && prop.units > tierCap);

  // What actually prevents a create, decided by the server (billingCheck.js)
  // with the same rules the create endpoint enforces — NOT simply "any failing
  // check". An expired card is a failing check but does not block: nothing is
  // charged until the start date, and the customer needs billing set up before
  // they can reach the Stripe portal to replace the card.
  // `canCreate` absent ⇒ the deployed functions predate it, so fall back to the
  // old rule; the two deploy separately.
  const createBlockers = (report && report.createBlockers) || [];
  const canCreate = report
    ? (report.canCreate === undefined ? report.overall !== 'fail' : !!report.canCreate)
    : false;
  const cardExpired = !!(report && report.stripe && report.stripe.paymentMethod
    && report.stripe.paymentMethod.expired);

  // Earliest permissible first payment. The server already refuses a past date;
  // with an expired card it also refuses today, because the customer has had no
  // chance to replace the card and the first payment would fail on creation.
  // Mirrored here so the picker cannot offer a date the endpoint would reject.
  // (UTC, matching the server's day boundary.)
  const minStartDate = toDateInputValue(Date.now() + (cardExpired ? 24 * 3600 * 1000 : 0));
  const startDateTooSoon = !!(startDate && startDate < minStartDate);

  // Did the expired-card week-out floor override the account's own proposed
  // start? If so the pre-filled date disagrees with the "Free months · delivered"
  // row above it, and the form should say why rather than leave the admin to
  // reconcile the two.
  // Requires a proposed date to have been overridden: with no delivery date on
  // the account there is no date to reconcile against, and the `start_date`
  // check row already says so.
  const weekOutApplied = !!(cardExpired && prop && prop.defaultStartMs
    && prop.defaultStartMs < Date.now() + 7 * 24 * 3600 * 1000);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-3xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0 border-b">
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="w-4 h-4" />
            Billing
            {report && <Badge variant={stateBadge.variant} className="text-xs">{stateBadge.label}</Badge>}
            {report && report.overall === 'fail' && (
              <Badge variant="destructive" className="text-xs">Checks failing</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-7 w-7 p-0" title="Reload">
              <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-4 pb-6">
          <div className="text-xs text-muted-foreground break-all mb-3">
            {accountEmail} · <span className="font-mono">{accountUUID}</span>
          </div>

          {loading && <div className="text-sm text-muted-foreground text-center py-8">Loading billing…</div>}
          {error && <div className="text-sm text-red-500 text-center py-8 break-all">{error}</div>}

          {report && (
            <>
              {report.account.cancelRequested && (
                <div className="mb-3 rounded border border-red-300 bg-red-50 text-red-800 text-sm p-2.5">
                  <b>Customer requested cancellation</b>
                  {report.account.cancelRequestedAtMs ? ` on ${fmtDate(report.account.cancelRequestedAtMs)}` : ''}.
                  Arrange the Hearth return, then cancel the subscription in Stripe.
                </div>
              )}

              {/* ── Purchase & promise ─────────────────────────── */}
              <SectionTitle>Purchase &amp; promise (at booking)</SectionTitle>
              <Row label="Batch / model">{report.account.batch || '—'} · {p.model || '?'} · {p.discountType || '?'}</Row>
              <Row label="Households (units)">{p.deviceCount}</Row>
              <Row label="Advertised price">
                {p.model === 'perUnit'
                  ? `${fmtMoney(p.fullUnitPriceMinor, p.currency)}/unit → ${fmtMoney(p.fullUnitPriceMinor * p.deviceCount, p.currency)}/mo`
                  : `${fmtMoney(p.fullUnitPriceMinor, p.currency)}/mo`}
              </Row>
              <Row label="Promised price">
                <b>{fmtMoney(p.monthlyChargeMinor, p.currency)}/mo</b>
                {p.model === 'perUnit' ? ` (${fmtMoney(p.discountedUnitPriceMinor, p.currency)} × ${p.deviceCount})` : ''}
              </Row>
              <Row label="Free months">{p.freeMonths}{prop && prop.deliveredMs ? ` · delivered ${fmtDate(prop.deliveredMs)}` : ''}</Row>

              {/* ── Subscription ───────────────────────────────── */}
              <SectionTitle>Subscription</SectionTitle>

              {createdOk && (
                <div className="mb-2 rounded border border-green-300 bg-green-50 text-green-800 text-sm p-2.5">
                  Subscription created successfully.
                </div>
              )}

              {(state === 'none' || state === 'canceled') && prop && (
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    {state === 'canceled' && (
                      <div className="text-amber-700 mb-1">
                        The previous subscription is canceled — this sets up a <b>new</b> one.
                      </div>
                    )}
                    Sets up a Stripe subscription that <b>starts billing on the chosen date</b> —
                    tier price with a lifetime discount, charged monthly to
                    {cardExpired ? " the customer's default card in Stripe" : ' the saved card'}.
                    Nothing is charged before the start date.
                  </div>
                  <div className="flex gap-3 items-end flex-wrap">
                    <div className="w-56">
                      <label className="text-xs font-medium mb-1 block">Subscription</label>
                      <Select value={tier} onValueChange={setTier}>
                        <SelectTrigger><SelectValue placeholder="Choose tier" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="warm">{TIER_LABEL.warm}</SelectItem>
                          <SelectItem value="glow">{TIER_LABEL.glow}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-32">
                      <label className="text-xs font-medium mb-1 block">Discount %</label>
                      <Input type="number" min="0" max="100" step="0.01" value={discountStr}
                        onChange={(e) => setDiscountStr(e.target.value)} />
                    </div>
                    <div className="w-44">
                      <label className="text-xs font-medium mb-1 block">First payment date</label>
                      {/* An expired card cannot start today — the server refuses
                          it, because the customer has had no chance to replace
                          the card and the first payment would fail at once. */}
                      <Input type="date" value={startDate} min={minStartDate}
                        onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                  </div>

                  {preview && (
                    <div className={`text-sm rounded border p-2.5 ${previewMatchesPromise ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
                      {TIER_LABEL[tier] ? TIER_LABEL[tier].split(' (')[0] : tier} {fmtMoney(preview.headline, preview.currency)}/mo
                      {' − '}{preview.pct}% = <b>{fmtMoney(preview.monthly, preview.currency)}/mo</b>,
                      first payment {startDate ? fmtDate(Date.parse(startDate)) : '—'}, then monthly on that day.
                      {previewMatchesPromise
                        ? ' Matches the promised price.'
                        : ` Promise was ${fmtMoney(p.monthlyChargeMinor, p.currency)}/mo — differs.`}
                    </div>
                  )}
                  {(prop.freePeriodElapsed || weekOutApplied) && (
                    <div className="text-xs text-amber-700">
                      {prop.freePeriodElapsed
                        ? `Note: delivered + free months (${fmtDate(prop.defaultStartMs)}) is already in the past — `
                        : `Note: delivered + free months would put the first payment at ${fmtDate(prop.defaultStartMs)} — `}
                      the date has been defaulted to {cardExpired ? 'a week out' : 'tomorrow'};
                      adjust as agreed with the customer.
                    </div>
                  )}

                  {/* The expired card never blocks — but something else might,
                      and this notice must not promise an action the button
                      below refuses. */}
                  {cardExpired && (
                    <div className="text-xs text-amber-800 rounded border border-amber-300 bg-amber-50 p-2.5">
                      <b>The card on file has expired.</b>{' '}
                      {canCreate
                        ? 'You can still set the subscription up — nothing is charged until the first payment date, which we default to at least a week out so the customer has time to act (one day is the hard minimum, not a recommendation). '
                        : 'It is not what is blocking the setup below. Once the blockers are resolved the subscription can still be set up. '}
                      The subscription will deliberately <b>not</b> be tied to the expired card: it
                      will charge whichever card is default on the Stripe customer at that point.
                      Email the customer to sign in and update their card via
                      {' '}<i>Manage billing &amp; invoices</i> before the first payment date, or
                      that payment will fail.
                    </div>
                  )}

                  {!canCreate && (
                    <div className="text-xs text-red-700 rounded border border-red-300 bg-red-50 p-2.5">
                      <b>A subscription can't be set up yet:</b>
                      {createBlockers.length > 0 ? (
                        <ul className="list-disc ml-4 mt-1 space-y-0.5">
                          {createBlockers.map((b) => (
                            <li key={b.id + b.label} className="break-all">
                              {b.label}{b.detail ? ` — ${b.detail}` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        // No list from the server (older functions deploy) —
                        // a blocked button must still carry a reason.
                        <span> resolve the failing checks below first.</span>
                      )}
                    </div>
                  )}

                  {startDateTooSoon && (
                    <div className="text-xs text-red-700">
                      {cardExpired
                        ? 'With an expired card the first payment must be at least a day out, so the customer has a chance to update it.'
                        : 'The first payment date cannot be in the past.'}
                    </div>
                  )}

                  {!confirming ? (
                    <Button
                      onClick={() => setConfirming(true)}
                      disabled={!preview || !startDate || !canCreate || startDateTooSoon}
                      className="flex items-center gap-2"
                      title={canCreate ? '' : (createBlockers.length
                        ? `Blocked by: ${createBlockers.map((b) => b.label).join(', ')}`
                        : 'Resolve failing checks first')}
                    >
                      <PlayCircle className="w-4 h-4" />
                      Start subscription…
                    </Button>
                  ) : (
                    <div className="rounded border border-blue-300 bg-blue-50 p-3 space-y-2">
                      {overCap && (
                        <div className="text-xs text-red-700">
                          <b>Warning:</b> this account has {prop.units} households — more than the {tier} cap
                          of {tierCap}. Confirming overrides the cap check.
                        </div>
                      )}
                      <div className="text-sm">
                        <b>Confirm:</b> create a subscription for <b>{accountEmail}</b> —
                        {' '}{tier} at {preview ? fmtMoney(preview.monthly, preview.currency) : '—'}/mo
                        ({preview ? preview.pct : '—'}% off {preview ? fmtMoney(preview.headline, preview.currency) : '—'}),
                        first payment on <b>{fmtDate(Date.parse(startDate))}</b>,
                        {cardExpired
                          ? ' charged to whichever card is default on the Stripe customer at that point.'
                          : ' charged to the saved card.'}
                        {' '}The discount lasts for the life of the subscription.
                      </div>
                      {cardExpired && (
                        <div className="text-xs text-amber-800">
                          The card on file has <b>expired</b>, so this subscription will not be tied
                          to it. The customer must add a valid card before
                          {' '}<b>{fmtDate(Date.parse(startDate))}</b> or the first payment will fail.
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button onClick={handleCreate} disabled={creating} className="flex items-center gap-2">
                          {creating ? 'Creating…' : 'Confirm & start'}
                        </Button>
                        <Button variant="outline" onClick={() => setConfirming(false)} disabled={creating}>
                          Back
                        </Button>
                      </div>
                    </div>
                  )}
                  {createError && <div className="text-sm text-red-600 break-all">{createError}</div>}
                </div>
              )}

              {state !== 'none' && (
                <div>
                  {report.activated && (
                    <>
                      <Row label="Plan">
                        {report.activated.tier} · {report.activated.discountPercent}% off →
                        {' '}<b>{fmtMoney(report.activated.monthlyMinor, p.currency)}/mo</b>
                      </Row>
                      <Row label="Set up">
                        {fmtDate(report.activated.activatedAtMs)} by {report.activated.activatedByEmail || '?'}
                      </Row>
                      {/* This subscription bills differently from every other
                          one — say so where someone chasing a failed first
                          payment would actually look. */}
                      {report.activated.paymentMethodPinned === false && (
                        <Row label="Payment method">
                          <span className="text-amber-700">
                            Not tied to the saved card — it had expired at setup, so this charges
                            whichever card is default on the Stripe customer.
                          </span>
                        </Row>
                      )}
                    </>
                  )}
                  {state === 'scheduled' && (
                    <Row label="Starts">
                      <b>{fmtDate(report.nextPayment && report.nextPayment.dateMs)}</b>
                      {' '}· first payment {report.nextPayment ? fmtMoney(report.nextPayment.amountMinor, report.nextPayment.currency) : '—'}
                      <div className="text-xs text-muted-foreground">
                        The customer's Stripe portal shows no subscription until the start date; our webapp shows the start date.
                      </div>
                    </Row>
                  )}
                  {(state === 'active' || state === 'trialing') && (
                    <Row label="Next payment">
                      <b>{fmtDate(report.nextPayment && report.nextPayment.dateMs)}</b>
                      {' '}· {report.nextPayment ? fmtMoney(report.nextPayment.amountMinor, report.nextPayment.currency) : '—'}
                    </Row>
                  )}
                  {['past_due', 'unpaid', 'incomplete'].includes(state) && (
                    <div className="my-2 rounded border border-red-300 bg-red-50 text-red-800 text-sm p-2.5">
                      Payment problem: subscription is <b>{state}</b>. Stripe retries automatically; the
                      customer can update their card via the portal (their webapp has a manage-billing button).
                    </div>
                  )}
                  <Row label="Card">
                    {s.paymentMethod && s.paymentMethod.exists
                      ? `${s.paymentMethod.brand} ••${s.paymentMethod.last4} (exp ${s.paymentMethod.expMonth}/${s.paymentMethod.expYear})${s.paymentMethod.expired ? ' — EXPIRED' : s.paymentMethod.expiringSoon ? ' — expiring soon' : ''}`
                      : 'missing'}
                  </Row>
                  <Row label="Stripe ids">
                    {s.schedule ? (
                      <>sched{' '}
                        <a href={`https://dashboard.stripe.com/subscription_schedules/${s.schedule.id}`}
                          target="_blank" rel="noopener noreferrer"
                          title="Open in Stripe dashboard (pause/cancel lives there)"
                          className="font-mono text-blue-500 hover:underline">{s.schedule.id}</a>
                        {' '}({s.schedule.status}){' '}
                      </>
                    ) : null}
                    {s.subscription ? (
                      <>sub{' '}
                        <a href={`https://dashboard.stripe.com/subscriptions/${s.subscription.id}`}
                          target="_blank" rel="noopener noreferrer"
                          title="Open in Stripe dashboard (pause/cancel lives there)"
                          className="font-mono text-blue-500 hover:underline">{s.subscription.id}</a>
                        {' '}({s.subscription.status})
                      </>
                    ) : null}
                    {!s.schedule && !s.subscription ? '—' : null}
                  </Row>

                  {s.invoices && s.invoices.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium mb-1">Payments</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground text-left">
                            <th className="py-0.5 pr-2 font-medium">Date</th>
                            <th className="py-0.5 pr-2 font-medium">Amount</th>
                            <th className="py-0.5 pr-2 font-medium">Status</th>
                            <th className="py-0.5 font-medium">Invoice</th>
                          </tr>
                        </thead>
                        <tbody>
                          {s.invoices.map((inv) => (
                            <tr key={inv.id} className={inv.failed ? 'text-red-600' : ''}>
                              <td className="py-0.5 pr-2">{fmtDate(inv.createdMs)}</td>
                              <td className="py-0.5 pr-2">{fmtMoney(inv.amountDueMinor, inv.currency)}</td>
                              <td className="py-0.5 pr-2">
                                {inv.paid ? 'paid' : inv.failed ? `FAILED (${inv.attemptCount} attempts)` : inv.status}
                              </td>
                              <td className="py-0.5">
                                {inv.hostedInvoiceUrl
                                  ? <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">view</a>
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Cancellation record ────────────────────────── */}
              {canceledButUnlinked && (
                <div className="mt-3 rounded border border-amber-300 bg-amber-50 text-amber-800 text-sm p-2.5">
                  Stripe reports a canceled subscription for this customer, but no subscription is
                  recorded against this account, so there is nothing to mark as cancelled here. The
                  subscription was most likely created directly in the Stripe dashboard — check the
                  Stripe ids above.
                </div>
              )}
              {/* Showing and clearing an EXISTING record is gated on the
                  record itself; only the offer to create one is gated on
                  hasStoredSubscription — mirroring the endpoint, which
                  enforces that check on the record path only and always
                  allows an undo. */}
              {(canceled || hasSubscriptionRecord) && (
                <div className="mt-3">
                  {canceled ? (
                    <div className="rounded border border-gray-300 bg-gray-50 text-gray-800 text-sm p-2.5">
                      <b>Recorded as cancelled</b> — effective {fmtDate(canceled.atMs)}.
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Recorded by {canceled.byEmail || '?'}
                        {canceled.recordedAtMs ? ` on ${fmtDate(canceled.recordedAtMs)}` : ''}
                        {canceled.reason ? ` · ${canceled.reason}` : ''}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        This is our own record — it drives the admin Billing view and changes nothing in Stripe.
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        disabled={cancelSaving}
                        onClick={() => handleSetCanceled({ clear: true })}
                      >
                        {cancelSaving ? 'Clearing…' : 'Clear this record'}
                      </Button>
                    </div>
                  ) : (
                    <>
                      {state === 'canceled' && (
                        <div className="rounded border border-amber-300 bg-amber-50 text-amber-800 text-sm p-2.5 mb-2">
                          Stripe reports this subscription as canceled but nothing is recorded here.
                          Record it, or the admin Billing view keeps showing this account as running.
                        </div>
                      )}
                      {!cancelOpen ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setCancelDate(toDateInputValue(Date.now())); setCancelError(null); setCancelOpen(true); }}
                          className="flex items-center gap-2"
                        >
                          <Ban className="w-4 h-4" />
                          Mark as cancelled…
                        </Button>
                      ) : (
                        <div className="rounded border border-blue-300 bg-blue-50 p-3 space-y-2">
                          <div className="text-xs text-muted-foreground">
                            Records that this subscription was cancelled, so the admin Billing view stops
                            showing it as running. <b>This does not cancel anything</b> — do that in the
                            Stripe dashboard first. The record can be cleared again if you mis-mark it.
                          </div>
                          <div className="flex gap-3 items-end flex-wrap">
                            <div className="w-44">
                              <label className="text-xs font-medium mb-1 block">Cancelled on</label>
                              <Input type="date" value={cancelDate} onChange={(e) => setCancelDate(e.target.value)} />
                            </div>
                            <div className="flex-1 min-w-[200px]">
                              <label className="text-xs font-medium mb-1 block">Reason (optional)</label>
                              <Input
                                type="text"
                                maxLength={500}
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="e.g. Hearth returned"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleSetCanceled({ clear: false })} disabled={!cancelDate || cancelSaving}>
                              {cancelSaving ? 'Recording…' : 'Record cancellation'}
                            </Button>
                            <Button variant="outline" onClick={() => { setCancelOpen(false); setCancelError(null); }} disabled={cancelSaving}>
                              Back
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {cancelError && <div className="text-sm text-red-600 break-all mt-1">{cancelError}</div>}
                </div>
              )}

              {/* ── Customer visibility ────────────────────────── */}
              <SectionTitle>Customer webapp</SectionTitle>
              <div className="flex items-center gap-3 py-1">
                <Switch checked={visible} onCheckedChange={handleVisibilityToggle} disabled={visibleSaving} />
                <div className="text-sm">
                  Show billing info in the customer's webapp Account tab
                  <div className="text-xs text-muted-foreground">
                    Off by default. Turn on when the subscription is set up — the customer then sees their plan,
                    price, next payment, a manage-billing button and a cancel-request button.
                  </div>
                  {visibleError && (
                    <div className="text-xs text-red-600 mt-1">{visibleError}</div>
                  )}
                </div>
              </div>

              {/* ── Checks ─────────────────────────────────────── */}
              <SectionTitle>Checks</SectionTitle>
              <div className="divide-y divide-muted/30">
                {report.checks.map((c, i) => <CheckRow key={i} check={c} />)}
              </div>

              {/* ── Notes ──────────────────────────────────────── */}
              <SectionTitle>Notes (internal — never visible to the customer)</SectionTitle>
              <div className="flex gap-2 items-start mb-2">
                <textarea
                  className="flex-1 border rounded-md p-2 text-sm min-h-[60px] bg-background"
                  placeholder="Add a note about this account…"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <Button onClick={handleAddNote} disabled={!noteText.trim() || noteSaving} className="flex items-center gap-2">
                  <StickyNote className="w-4 h-4" />
                  {noteSaving ? 'Adding…' : 'Add note'}
                </Button>
              </div>
              {noteSaveError && (
                <div className="text-xs text-red-600 mb-2">{noteSaveError}</div>
              )}
              {notesLoadError ? (
                <div className="text-xs text-amber-700">
                  Notes are unavailable right now (permissions or connectivity — are the latest Firestore
                  rules deployed?). Server-recorded notes are still being written and will appear once
                  access is restored.
                </div>
              ) : notes.length === 0 ? (
                <div className="text-xs text-muted-foreground">No notes yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {notes.map((n) => (
                    <div key={n.id} className="text-sm border-l-2 border-muted pl-2">
                      <div className="whitespace-pre-wrap">{n.text}</div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                        {/* Comment revisions from the Billing view share this
                            stream — label them so the history reads clearly. */}
                        {n.kind === 'comment' && (
                          <span className="rounded-sm bg-muted px-1 py-0.5 text-[10px] uppercase tracking-wide">
                            comment
                          </span>
                        )}
                        <span>
                          {n.adminEmail || '?'} · {n.createdAt && n.createdAt.toDate ? fmtDate(n.createdAt.toDate().getTime()) : '…'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
